/**
 * The four numbers from step 2, as a strip across the top of step 3 (UX review, 17 Oct 2026).
 *
 * STRIP, NOT RAIL (owner decision, 17 Oct 2026). In engine mode the right-hand rail repeated step 2's
 * position in different words beside every plan. The same four figures, once, across the top, with
 * the words step 2 used, so a plan's "+$61,507 vs doing nothing" has its comparison in view without a
 * second column. Manual Control keeps its rail: there the figures move as each slider is dragged.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';

interface DoNothingStripProps {
    careDaily: number;
    careAnnual: number;
    enteringName: string;
    pension: number;
    /** The pension today, before care — "up from $32,838 once care starts". */
    pensionToday?: number | null;
    /** Cashflow after care each year; negative is a shortfall. */
    cashflowAfterCare: number;
    legacy: number | null;
    horizonYears: number;
    /** Opens the "things that change the answer" dialog on the horizon. */
    onWhyYears?: () => void;
    /**
     * The first cell in another module's terms (Support at home: "Their contribution … a year"), in
     * place of aged care's daily care cost.
     */
    costCell?: { label: string; value: number; unit: string; note: React.ReactNode } | null;
    /** The pension cell's label and note, where "once care starts" is not the module's wording. */
    pensionLabel?: string;
    pensionNoteText?: React.ReactNode;
    /** The cashflow note under "short by" / "left over". */
    cashflowNotes?: { short: React.ReactNode; over: React.ReactNode };
    /** The line above the strip; null hides it (Support at home's step 2 has its own heading). */
    caption?: React.ReactNode | null;
    /** Larger figures (step 2, where the four numbers ARE the screen). */
    large?: boolean;
}

const Cell = ({ label, value, unit, note, tone = 'neutral', large = false }: { label: string; value: string; unit?: string; note: React.ReactNode; tone?: 'neutral' | 'good' | 'bad'; large?: boolean }) => (
    <div className={`${large ? 'px-5 py-5' : 'px-4 py-3.5'} first:pl-5 last:pr-5 border-slate-200 md:border-l md:first:border-l-0`}>
        <p className={`${large ? 'text-[13px]' : 'text-[12px]'} font-semibold text-slate-500`}>{label}</p>
        <p className={`mt-0.5 ${large ? 'text-[28px]' : 'text-[22px]'} font-bold leading-none tabular-nums tracking-tight ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>
            {value}{unit && <span className="ml-1 text-[12px] font-semibold text-slate-500">{unit}</span>}
        </p>
        <p className="mt-1 text-[12px] text-slate-500">{note}</p>
    </div>
);

const DoNothingStrip = ({ careDaily, careAnnual, enteringName, pension, pensionToday = null, cashflowAfterCare, legacy, horizonYears, onWhyYears, costCell = null, pensionLabel, pensionNoteText, cashflowNotes, caption, large = false }: DoNothingStripProps) => {
    const short = cashflowAfterCare < 0;
    const pensionNote = pensionToday != null && Math.round(pensionToday) !== Math.round(pension)
        ? `${Math.round(pension) > Math.round(pensionToday) ? 'up' : 'down'} from ${formatCurrency(Math.round(pensionToday))} once care starts`
        : 'once care starts';
    return (
        <div data-testid="do-nothing-strip">
            {/* Sentence case (owner: no caps eyebrows). */}
            {caption !== null && <p className="text-[13px] text-slate-500">{caption ?? 'Compared with paying daily — the four numbers from step 2'}</p>}
            <div className={`${caption !== null ? 'mt-2' : ''} grid grid-cols-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card sm:grid-cols-2 md:grid-cols-4`}>
                {costCell
                    ? <Cell large={large} label={costCell.label} value={formatCurrency(Math.round(costCell.value))} unit={costCell.unit} note={costCell.note} />
                    : <Cell large={large} label="Care would cost" value={formatCurrency(Math.round(careDaily))} unit="a day" note={`${formatCurrency(Math.round(careAnnual))} a year`} />}
                <Cell large={large} label={pensionLabel ?? `${enteringName}'s Age Pension`} value={formatCurrency(Math.round(pension))} unit="a year" note={pensionNoteText ?? pensionNote} />
                <Cell
                    large={large}
                    label={short ? 'Each year, short by' : 'Left over each year'}
                    value={formatCurrency(Math.abs(Math.round(cashflowAfterCare)))}
                    note={short ? (cashflowNotes?.short ?? 'met from savings') : (cashflowNotes?.over ?? 'after care and living costs')}
                    tone={short ? 'bad' : 'good'}
                />
                <Cell
                    large={large}
                    label="Left to the family"
                    value={legacy != null ? formatCurrency(Math.round(legacy)) : '—'}
                    note={<>after about {horizonYears} {horizonYears === 1 ? 'year' : 'years'}{onWhyYears && <> · <button type="button" onClick={onWhyYears} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">why {horizonYears}?</button></>}</>}
                />
            </div>
        </div>
    );
};

export default DoNothingStrip;
